import { Autocomplete } from '@mantine/core';
import { useCallback, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useToasts } from 'react-toast-notifications';
import { Onboarding } from '../../types/company';
import { useAxios } from '../../utils/hooks';

const TOOLS = ['Bubble', 'Wordpress', 'Wix', 'HTML/JS', 'Webflow'];
const MORE_TOOLS = [
    ...TOOLS,
    'ClickFunnels',
    'Super.so',
    'Candu',
    'Enzyme',
    'Potion',
    'Strikingly',
    'Typedream',
    'duda',
    'grapedrop',
    'mmmpage',
    'questai',
    'solidpixels',
    'tilda',
    'unstack',
    'versoly',
    'EditorX',
    'Popsy',
    'Sparkle',
    'Vev',
    'boundless',
    'ghost',
    'gridstudio',
    'pory',
    'sheet2site',
    'squarespace',
    'universe',
    'usmo',
    'wappler',
];

interface AboutYouForm {
    tool: string;
}

interface Props {
    onNextStep: () => void;
}

export default function AboutYou({ onNextStep }: Props) {
    const axios = useAxios();
    const { addToast } = useToasts();

    const [showMore, setShowMore] = useState(false);
    const handleShowMore = () => setShowMore(true);

    const handleSubmit = useCallback(
        (data: AboutYouForm) => {
            if (!axios || !axios.current) {
                return Promise.resolve();
            }

            return axios.current?.post<Onboarding>('/company/onboarding', data).then(
                res => {
                    onNextStep();
                },
                err => {
                    addToast('We did something wrong. Please try again.', { appearance: 'error' });
                }
            );
        },
        [addToast, axios, onNextStep]
    );

    return (
        <Form<AboutYouForm> onSubmit={handleSubmit}>
            {({ handleSubmit, submitting, invalid, values }) => (
                <ul className="grid grid-cols-3 gap-5 m-10 max-w-md mx-auto">
                    {!showMore && (
                        <>
                            {TOOLS.map(tool => (
                                <Field key={tool} name="tool" type="radio" value={tool}>
                                    {({ input: { onChange, ...inputRest }, meta }) => (
                                        <li className="relative">
                                            <label className="flex text-center justify-center p-5 bg-white border border-gray-300 rounded-lg cursor-pointer focus:outline-none hover:bg-gray-50 peer-checked:ring-green-500 peer-checked:ring-2 peer-checked:border-transparent">
                                                <input
                                                    onChange={e => {
                                                        onChange(e);
                                                        handleSubmit();
                                                    }}
                                                    {...inputRest}
                                                    className="sr-only peer"
                                                />
                                                <span>{tool}</span>
                                            </label>
                                        </li>
                                    )}
                                </Field>
                            ))}
                            <li className="relative">
                                <input className="sr-only peer" />
                                <label
                                    onClick={handleShowMore}
                                    className="flex text-center justify-center p-5 bg-white border border-gray-300 rounded-lg cursor-pointer focus:outline-none hover:bg-gray-50 peer-checked:ring-green-500 peer-checked:ring-2 peer-checked:border-transparent"
                                >
                                    Other
                                </label>
                            </li>
                        </>
                    )}

                    {showMore && (
                        <>
                            <div className="col-span-3">
                                <Field name="tool" type="radio">
                                    {({ input: { onChange, ...inputRest } }) => (
                                        <Autocomplete
                                            label="Type the name of your website builder"
                                            placeholder="e.g. Squarespace"
                                            onItemSubmit={item => {
                                                onChange(item.value);
                                                handleSubmit();
                                            }}
                                            data={MORE_TOOLS}
                                        />
                                    )}
                                </Field>
                            </div>

                            <div className="col-span-3 text-center">
                                <button onClick={onNextStep} className="link text-sm">
                                    Can't find yours?
                                </button>
                            </div>
                        </>
                    )}
                </ul>
            )}
        </Form>
    );
}
